import React, { useState } from "react";
import { X, ExternalLink } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const TASK_EMOJIS = {
  buy_fvt: "🎟️",
  story_or_tweet: "📣",
  repost: "🔁",
  tag_friends: "👥",
  like_social: "❤️",
  likes_on_repost: "⭐",
};

const TASK_COLORS = {
  buy_fvt: "#c8f135",
  story_or_tweet: "#ff3dac",
  repost: "#3dffea",
  tag_friends: "#ff8c00",
  like_social: "#a78bfa",
  likes_on_repost: "#60a5fa",
};

export default function TaskModal({ task, completing, onClose, onComplete }) {
  const [submissionData, setSubmissionData] = useState("");
  const [done, setDone] = useState(false);

  const emoji = TASK_EMOJIS[task.task_type] || "✅";
  const color = TASK_COLORS[task.task_type] || "#c8f135";
  const needsUrl = task.completion_method === "url_submit";
  const needsUsernames = task.completion_method === "username_entry";
  const isSelfConfirm = task.completion_method === "self_confirm";

  const canSubmit = isSelfConfirm || (needsUrl && submissionData.trim()) || (needsUsernames && submissionData.trim());

  const handleSubmit = async () => {
    setDone(true);
    await onComplete(task, submissionData);
  };

  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
        style={{ background: "rgba(0,0,0,0.88)", backdropFilter: "blur(8px)" }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          key="modal"
          initial={{ y: 60, opacity: 0, scale: 0.97 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 60, opacity: 0, scale: 0.97 }}
          transition={{ type: "spring", damping: 22, stiffness: 280 }}
          className="w-full max-w-md rounded-3xl overflow-hidden"
          style={{ background: "#111111", border: `1px solid ${color}30` }}
        >
          {/* Coloured top accent line */}
          <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${color}, ${color}80)` }} />

          {/* Header */}
          <div className="p-6 pb-4" style={{ borderBottom: "1px solid #1a1a1a" }}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.15, type: "spring", stiffness: 300 }}
                  className="text-3xl"
                >
                  {emoji}
                </motion.span>
                <h2 className="font-black text-white text-lg leading-tight" dir="rtl">{task.title}</h2>
              </div>
              <button onClick={onClose} className="p-1 flex-shrink-0 hover:opacity-70 transition-opacity">
                <X className="w-5 h-5" style={{ color: "rgba(255,255,255,0.4)" }} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-6" dir="rtl">
            <p className="text-sm leading-relaxed mb-5" style={{ color: "rgba(255,255,255,0.55)" }}>
              {task.description}
            </p>

            {/* Points preview */}
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="rounded-xl p-3 mb-5 flex items-center justify-between"
              style={{ background: `${color}10`, border: `1px solid ${color}30` }}
            >
              <span className="text-sm font-bold" style={{ color: "rgba(255,255,255,0.5)" }}>תרוויח</span>
              <span className="font-black text-2xl" style={{ color }}>
                +{task.max_points || task.points_per_action} נקודות
              </span>
            </motion.div>

            {/* Open link button */}
            {task.action_url && (
              <a
                href={task.action_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold text-sm mb-4 transition-all hover:bg-[#222]"
                style={{ background: "#1a1a1a", color: "rgba(255,255,255,0.7)", border: "1px solid #2a2a2a" }}
              >
                פתח קישור למשימה
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}

            {/* URL input */}
            {needsUrl && (
              <div className="mb-4">
                <label className="block text-xs font-bold mb-2" style={{ color: "rgba(255,255,255,0.4)" }}>
                  הדבק קישור לסטורי / טוויט שלך
                </label>
                <input
                  type="url"
                  value={submissionData}
                  onChange={e => setSubmissionData(e.target.value)}
                  placeholder="https://x.com/..."
                  className="w-full rounded-xl px-4 py-3 text-sm font-medium outline-none"
                  style={{ background: "#1a1a1a", border: `1px solid ${submissionData ? color + "60" : "#2a2a2a"}`, color: "#fff", transition: "border-color 0.2s" }}
                />
              </div>
            )}

            {/* Usernames input */}
            {needsUsernames && (
              <div className="mb-4">
                <label className="block text-xs font-bold mb-2" style={{ color: "rgba(255,255,255,0.4)" }}>
                  הכנס שמות משתמש שתייגת (מופרדים בפסיק)
                </label>
                <textarea
                  value={submissionData}
                  onChange={e => setSubmissionData(e.target.value)}
                  placeholder="@user1, @user2, ..."
                  rows={3}
                  className="w-full rounded-xl px-4 py-3 text-sm font-medium outline-none resize-none"
                  style={{ background: "#1a1a1a", border: `1px solid ${submissionData ? color + "60" : "#2a2a2a"}`, color: "#fff", transition: "border-color 0.2s" }}
                />
              </div>
            )}

            {/* CTA */}
            <motion.button
              onClick={handleSubmit}
              disabled={!canSubmit || completing}
              whileTap={{ scale: canSubmit && !completing ? 0.96 : 1 }}
              className="w-full py-4 rounded-xl font-black text-base transition-all disabled:opacity-40"
              style={{
                background: canSubmit && !completing ? color : "#2a2a2a",
                color: canSubmit && !completing ? (color === "#c8f135" || color === "#3dffea" ? "#000" : "#fff") : "rgba(255,255,255,0.3)",
              }}
            >
              {completing ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-transparent rounded-full animate-spin" style={{ borderTopColor: "currentColor" }} />
                  שומר...
                </span>
              ) : isSelfConfirm ? "✅ אישור — ביצעתי!" : "שלח ואשר"}
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}